import type { BankerLead } from '../types/expert';

export interface SymbolGroup {
  symbol: string;
  direction: string;
  bestScore: number;
  latestAt: number;
  leads: BankerLead[];
}

export interface ExchangeGroup {
  exchange: string;
  latestAt: number;
  symbols: SymbolGroup[];
}

export function extractExchange(lead: BankerLead): string {
  if (lead.exchange) return lead.exchange.toUpperCase();
  const parts = lead.symbol?.split('.');
  return parts && parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'OTHER';
}

function timestamp(lead: BankerLead): number | null {
  if (!lead.created_at) return null;
  const value = new Date(lead.created_at).getTime();
  return Number.isNaN(value) ? null : value;
}

function compareNewestFirst(a: BankerLead, b: BankerLead): number {
  const aTime = timestamp(a);
  const bTime = timestamp(b);
  if (aTime === null && bTime === null) return b.id - a.id;
  if (aTime === null) return 1;
  if (bTime === null) return -1;
  return bTime - aTime || b.id - a.id;
}

export function sortLeadsNewestFirst(leads: BankerLead[]): BankerLead[] {
  return [...leads].sort(compareNewestFirst);
}

export function formatRelativeTime(dateStr: string, nowMs = Date.now()): string {
  const value = new Date(dateStr).getTime();
  if (Number.isNaN(value)) return 'time unavailable';

  const deltaMs = nowMs - value;
  const elapsed = deltaMs >= 0;
  const absSeconds = Math.floor(Math.abs(deltaMs) / 1000);
  if (absSeconds < 60) return elapsed ? 'just now' : 'in under a minute';
  if (absSeconds < 3600) return `${Math.floor(absSeconds / 60)}m ${elapsed ? 'ago' : 'from now'}`;
  if (absSeconds < 86400) return `${Math.floor(absSeconds / 3600)}h ${elapsed ? 'ago' : 'from now'}`;
  return `${Math.floor(absSeconds / 86400)}d ${elapsed ? 'ago' : 'from now'}`;
}

export function latestLeadIdBySymbol(leads: BankerLead[]): Map<string, number> {
  const latest = new Map<string, BankerLead>();
  for (const lead of leads) {
    const key = `${extractExchange(lead)}|${lead.symbol ?? ''}`;
    const current = latest.get(key);
    if (!current || compareNewestFirst(lead, current) < 0) latest.set(key, lead);
  }
  return new Map(Array.from(latest, ([key, lead]) => [key, lead.id]));
}

export function groupLeadsByExchangeThenSymbol(leads: BankerLead[]): ExchangeGroup[] {
  const byExchange = new Map<string, Map<string, BankerLead[]>>();

  for (const lead of leads) {
    const exchange = extractExchange(lead);
    const symbol = lead.symbol ?? '';
    if (!byExchange.has(exchange)) byExchange.set(exchange, new Map());
    const bySymbol = byExchange.get(exchange)!;
    if (!bySymbol.has(symbol)) bySymbol.set(symbol, []);
    bySymbol.get(symbol)!.push(lead);
  }

  return Array.from(byExchange.entries())
    .map(([exchange, symbolMap]) => {
      const symbols = Array.from(symbolMap.entries())
        .map(([symbol, symbolLeads]) => {
          const sorted = sortLeadsNewestFirst(symbolLeads);
          const latestAt = timestamp(sorted[0]) ?? 0;
          return {
            symbol,
            direction: sorted[0].direction ?? '',
            bestScore: Math.max(...sorted.map((l) => l.gate_score ?? 0)),
            latestAt,
            leads: sorted,
          };
        })
        .sort((a, b) => b.latestAt - a.latestAt);
      return {
        exchange,
        latestAt: symbols.length > 0 ? symbols[0].latestAt : 0,
        symbols,
      };
    })
    .sort((a, b) => b.latestAt - a.latestAt);
}
